import { useCallback, useEffect, useState } from 'react';

import { getStripePaymentMethods } from '../../services/payment-services';

import type { SavedPaymentMethodDisplay } from '../../components/modals/AddPaymentMethodModal';

/** Owns fetching/state of the user's saved Stripe payment methods for card payments. */
export const useSavedPaymentMethods = (isLoadingData: boolean) => {
    const [savedPaymentMethods, setSavedPaymentMethods] = useState<SavedPaymentMethodDisplay[]>([]);
    const [, setSavedPaymentMethodsLoading] = useState(false);

    const fetchStripePaymentMethods = useCallback(async () => {
        setSavedPaymentMethodsLoading(true);
        try {
            const response = await getStripePaymentMethods(false, {
                type: 'card',
                limit: 50,
            });
            if (response.statusCode === 200 && Array.isArray(response.data)) {
                // Keep funding and country as-is from backend; do NOT default to 'credit' for unknown funding
                // Cards with missing funding will be blocked during payment validation
                const enrichedMethods = response.data.map(card => ({
                    ...card,
                    country: card.country || 'US',
                }));
                setSavedPaymentMethods(enrichedMethods);
            } else {
                setSavedPaymentMethods([]);
            }
        } catch (err) {
            console.error('Error fetching Stripe payment methods:', err);
            setSavedPaymentMethods([]);
        } finally {
            setSavedPaymentMethodsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isLoadingData) {
            fetchStripePaymentMethods();
        }
    }, [isLoadingData, fetchStripePaymentMethods]);

    return { savedPaymentMethods, fetchStripePaymentMethods };
};
